import type { Fetcher, FetcherParams } from '@graphiql/toolkit';
import type { WidgetProps } from '@takeshape/shape-form';
import { getProjectEndpoint } from 'app/branches/endpoints';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import { useIntrospectedSchema } from 'app/branches/hooks/use-introspected-schema';
import { getPreviewBranchServiceLayers } from 'app/branches/selectors';
import { getOptions } from 'app/common/components/widgets/select-query/select-query-hook';
import { useDebouncedChangeHandler } from 'app/common/hooks/use-debounced-change-handler';
import { getToken } from 'app/common/utils/get-token';
import { getCurrentProjectId } from 'app/project/selectors';
import { fromJS } from 'immutable';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

export type ToolQueryEditorOwnProps = WidgetProps<string>;

async function fetchGraphQL(
  projectId: string,
  branch: string,
  schemaHash: string | undefined,
  params: { query: string; variables?: Record<string, unknown> }
) {
  const endpoint = getProjectEndpoint(projectId, branch, schemaHash);
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-TakeShape-Token': getToken()!
    },
    body: JSON.stringify(params)
  });

  if (res.status !== 200) {
    throw new Error(`Status ${res.status}`);
  }

  return res.json();
}

// Config for getOptions: include mutations, local only (user's schema queries only)
const queryPickerConfig = fromJS({ includeMutations: true, localOnly: true });

export const useToolQueryEditor = (props: ToolQueryEditorOwnProps) => {
  const { value, onChange, onBlur } = props;

  const projectId = useSelector(getCurrentProjectId);
  const serviceLayers = useSelector(getPreviewBranchServiceLayers);
  const { activeRuntimeSchemaJSON } = useActiveSchema();
  const { schema, branch, schemaHash } = useIntrospectedSchema();

  // Fetcher for GraphiQLProvider — used for both autocomplete and execution
  const fetcherRef = useRef<Fetcher>();
  useEffect(() => {
    fetcherRef.current = (params: FetcherParams) =>
      fetchGraphQL(projectId, branch, schemaHash, {
        query: params.query,
        variables: params.variables ?? undefined
      });
  }, [projectId, branch, schemaHash]);

  const fetcher: Fetcher = useCallback((params: FetcherParams) => {
    return fetcherRef.current?.(params) ?? Promise.resolve({});
  }, []);

  // Debounced onChange to propagate query string changes to form store
  const debouncedOnChange = useDebouncedChangeHandler(
    useCallback(
      (queryString: string) => {
        onChange(queryString);
        onBlur?.();
      },
      [onChange, onBlur]
    ),
    500
  );

  const handleQueryEdit = useCallback(
    (query?: string) => {
      if (query !== undefined) {
        debouncedOnChange.run(query);
      }
    },
    [debouncedOnChange]
  );

  // Available queries/mutations for the quick picker
  const queryOptions = useMemo(
    () => getOptions(activeRuntimeSchemaJSON ?? undefined, serviceLayers, queryPickerConfig),
    [activeRuntimeSchemaJSON, serviceLayers]
  );

  return {
    ...props,
    query: value ?? '',
    schema,
    fetcher,
    handleQueryEdit,
    queryOptions,
    activeRuntimeSchemaJSON
  };
};

export type ToolQueryEditorProps = ReturnType<typeof useToolQueryEditor>;
